import {
  AnalysisJobAreaInputType,
  AnalysisJobItem,
  AnalysisJobStatus
} from "@/types/analysis";
import {
  AnalysisDatasetSchema,
  AnalysisDatasetSummary,
  GeneralSummary
} from "@/types/analysisSummary";
import {
  AnalysisResponse,
  AnalysisResultType,
  AnalysisStatus,
  DatasetDataType
} from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";

export const analysisResult: AnalysisResponse = {
  id: "279a3516-ecb8-4a8e-bee1-4b2a091cf70a",
  result: [
    {
      columns: [
        {
          data_type: DatasetDataType.TYPE_STRING,
          name: "grid_id"
        },
        {
          data_type: DatasetDataType.TYPE_GEOMETRY,
          name: "grid_geometry"
        },
        {
          data_type: DatasetDataType.TYPE_FLOAT,
          name: "score"
        },
        {
          data_type: DatasetDataType.TYPE_FLOAT,
          name: "Count of POI Education 2025_proportion"
        }
      ],
      dataset_id: "4f155754-79a5-46d4-9f3a-06ad3c23d890",
      name: "spatial analysis result",
      rows: [
        [
          "838c10fffffffff",
          "POLYGON((107.48839601232837 -6.782994916517139,107.34740416562394 -6.191251729443686,106.7393632059551 -6.041646022350608,106.27287358396106 -6.486988206543949,106.41680039214017 -7.07920508346019,107.0243002756687 -7.225632431288713,107.48839601232837 -6.782994916517139))",
          1000,
          1000
        ]
      ],
      spatial_aggregation_type: EOutputAnalysisType.GRID,
      dataset_size_kb: 100
    }
  ],
  status: AnalysisStatus.SUCCESS
};

export const mockAnalysisSummaryData: GeneralSummary = {
  data: {
    attributes: [
      {
        attribute_name: "population",
        data_type: DatasetDataType.TYPE_FLOAT,
        rows: [
          { catchment_id: "1", value: 1000 },
          { catchment_id: "2", value: 1500 }
        ]
      },
      {
        attribute_name: "income",
        data_type: DatasetDataType.TYPE_FLOAT,
        rows: [
          { catchment_id: "1", value: 50000 },
          { catchment_id: "2", value: 75000 }
        ]
      }
    ],
    general: {
      avg_score: 1250,
      max_score: 1500,
      median_score: 1250,
      min_score: 1000,
      total_rows: 2
    }
  }
};

const analysisDatasetSummaryColumns = [
  { name: "Label", data_type: DatasetDataType.TYPE_STRING },
  { name: "Value", data_type: DatasetDataType.TYPE_FLOAT }
] as const;

type AnalysisDatasetSummaryOverrides = Partial<
  Omit<
    AnalysisDatasetSummary,
    "rows" | "columns" | "analysis_id" | "dataset_id" | "dataset_size_kb"
  >
> & {
  rows?: AnalysisDatasetSummary["rows"];
  columns?: AnalysisDatasetSummary["columns"];
  analysis_id?: string;
  dataset_id?: string;
  dataset_size_kb?: number;
};

export const createMockAnalysisDatasetSummary = (
  rows: AnalysisDatasetSummary["rows"] = [],
  overrides: AnalysisDatasetSummaryOverrides = {}
): AnalysisDatasetSummary => {
  const {
    rows: overrideRows,
    columns,
    analysis_id = "mock-analysis-id",
    dataset_id = "mock-dataset-id",
    dataset_size_kb = 0,
    ...rest
  } = overrides;

  return {
    analysis_id,
    dataset_id,
    dataset_size_kb,
    columns:
      columns ?? analysisDatasetSummaryColumns.map((column) => ({ ...column })),
    rows: overrideRows ?? rows,
    ...rest
  };
};

export const mockAnalysisDatasetSummary = createMockAnalysisDatasetSummary([
  ["foo_label", 123.123]
]);

export const mockEmptyAnalysisDatasetSummary = createMockAnalysisDatasetSummary(
  []
);

export const mockAnalysisDatasetSchema: AnalysisDatasetSchema = {
  analysis_id: "mock-analysis-id",
  dataset_id: "mock-dataset-id",
  schema: [
    {
      id: "grid_id",
      attribute_name: "Grid ID",
      data_type: "string",
      unique_values_count: 3
    },
    {
      id: "category_id",
      attribute_name: "Category",
      data_type: "string",
      unique_values_count: 5
    },
    {
      id: "value_id",
      attribute_name: "Value",
      data_type: "float",
      unique_values_count: 10
    }
  ]
};

export const MOCK_GET_ANALYSIS_TEMPLATES_PAYLOAD = {
  page: 1,
  per_page: 10
};

export const MOCK_GET_ANALYSIS_TEMPLATES_REPONSE = {
  data: [
    {
      id: "13b1323c-5091-42b1-9519-61d81bff2103",
      short_description: "Desc Test Thematic Grid Geo Hash 001 17072025",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Thematic Grid Geo Hash 001 17072025"
    },
    {
      id: "05748df8-1547-4d34-9fc0-f424b7d39ccd",
      short_description: "Desc Test Combination H3 001",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Combination H3 001"
    },
    {
      id: "51ae7eb7-fc91-4ac6-a600-7eabcf25327f",
      short_description: "Desc Test Polygon Profiling 001",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Polygon Profiling 001"
    },
    {
      id: "c81da242-2d12-4954-9aba-84f9c1b50d33",
      short_description: "Desc Test Thematic Grid Geo Hash 001",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Thematic Grid Geo Hash 001"
    },
    {
      id: "17f5e1e2-1441-4594-8106-2703f087fc11",
      short_description: "Desc Test Combination Profiling",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Combination Profiling"
    },
    {
      id: "fba27962-f015-4269-b08c-54a8c6ef219b",
      short_description: "Desc Test Combination Grid H3 Lv 6",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Combination Grid H3 Lv 6"
    },
    {
      id: "c4c72b65-61c3-4657-b771-1f96a421a3ba",
      short_description: "Desc Test Combination Grid Geo Hash Lv 6",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Combination Grid Geo Hash Lv 6"
    },
    {
      id: "e982174c-ad9c-4833-93dd-c88fc7d6178e",
      short_description: "Desc Test Polygon Profiling",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Polygon Profiling"
    },
    {
      id: "12cc02b3-2793-45ed-a692-4699de45f353",
      short_description: "Desc Test POI Grid H3 Lv 6",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test POI Grid H3 Lv 6"
    },
    {
      id: "91bd6372-ec2b-42ec-8027-9b3b6b7627ab",
      short_description: "Desc Test Thematic Grid Geo Hash Lv 6",
      thumbnail_icon: {
        expiry_at: "2025-08-28T05:04:43Z",
        id: "ebf69b30-62fc-41bb-b00b-af3168034e82",
        url: "https://bvtdevorbis.blob.core.windows.net/dev-orbis/assets/assets"
      },
      title: "Test Thematic Grid Geo Hash Lv 6 More Than 3"
    }
  ],
  meta: {
    pagination: {
      next: 2,
      per_page: 10,
      prev: 0,
      total: 12,
      total_pages: 2
    }
  }
};

export const mockAnalysisJobList: AnalysisJobItem[] = [
  {
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_at: "2025-08-20T01:45:36.401014Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "b2ec4bcd-0de8-44b7-a04f-2054826f3141",
    name: "Completed Analysis",
    status: AnalysisJobStatus.SUCCESS,
    expired_at: "2030-08-15T06:45:36.401014Z"
  },
  {
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_at: "2025-07-14T06:45:36.401014Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "b2ec4bc3-0de8-44b7-a04f-2054826f3141",
    name: "Completed Analysis Expired",
    status: AnalysisJobStatus.SUCCESS,
    expired_at: "2025-07-15T06:45:36.401014Z"
  },
  {
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_at: "2025-08-20T03:30:00.778092Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "60ef98c7-10b4-4ffa-8027-f30be2c0eacb",
    name: "Running Analysis 1",
    status: AnalysisJobStatus.IN_PROGRESS,
    estimated_time_secs: 3600,
    expired_at: "2030-08-15T06:45:36.401014Z"
  },
  {
    output_type: "",
    input_type: "",
    created_at: "2025-08-20T03:30:00.778092Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "60ef98c7-1034-4ffa-8027-f30be2c0eacb",
    name: "Running Analysis 2",
    status: AnalysisJobStatus.IN_PROGRESS,
    estimated_time_secs: 0,
    expired_at: "2030-08-15T06:45:36.401014Z"
  },
  {
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_at: "2025-08-20T03:30:00.778092Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "60ef98c7-1334-4ffa-8027-f30be2c0eacb",
    name: "Running Analysis 3",
    status: AnalysisJobStatus.IN_PROGRESS,
    estimated_time_secs: 2000,
    expired_at: "2030-08-15T06:45:36.401014Z"
  },
  {
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_at: "2025-08-19T07:47:04.891362Z",
    created_by: "BVT",
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ],
    id: "162f905d-ad80-4ed8-b5cf-9144da672620",
    name: "Waiting Analysis",
    status: AnalysisJobStatus.QUEUED,
    expired_at: "2030-08-15T06:45:36.401014Z"
  }
];

export const mockAnalysisHistory: AnalysisJobItem[] = [
  {
    id: "b2ec4bcd-0de8-44b7-a04f-2054826f3141",
    name: "Completed Analysis",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "BVT",
    created_at: "2025-08-20T01:45:36.401Z",
    expired_at: "2030-08-15T06:45:36.401Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ]
  },
  {
    id: "b2ec4bc3-0de8-44b7-a04f-2054826f3141",
    name: "Completed Analysis Expired",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_by: "BVT",
    created_at: "2025-07-14T06:45:36.401Z",
    expired_at: "2025-07-15T06:45:36.401Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      {
        id: "59fb991b-de45-4e95-86b9-9ebba1e9c553",
        name: "QA Population By City"
      },
      {
        id: "60c9410f-ff10-4867-bfb1-54b018ef486a",
        name: "Mobility Heatmap 2025"
      }
    ]
  },
  {
    id: "c4aa112d-3de8-45ab-92af-9ebba1e9c553",
    name: "Market Demand Analysis",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Andi",
    created_at: "2025-11-10T04:30:00.000Z",
    expired_at: "2030-11-10T04:30:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      { id: "a1", name: "Retail Coverage 2025" },
      { id: "a2", name: "Population Density Layer" }
    ]
  },
  {
    id: "c5aa112d-3de8-45ab-92af-9ebba1e9c554",
    name: "Heatmap Mobility Result",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Siti",
    created_at: "2025-11-12T09:45:00.000Z",
    expired_at: "2030-11-12T09:45:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [{ id: "b1", name: "Mobility Heatmap 2025" }]
  },
  {
    id: "c6aa112d-3de8-45ab-92af-9ebba1e9c555",
    name: "Demographic Profiling 2025",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_by: "Andi",
    created_at: "2025-10-29T07:00:00.000Z",
    expired_at: "2030-10-29T07:00:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [{ id: "c1", name: "Population by Region" }]
  },
  {
    id: "c7aa112d-3de8-45ab-92af-9ebba1e9c556",
    name: "Competitor Site Mapping",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Rina",
    created_at: "2025-10-10T02:00:00.000Z",
    expired_at: "2030-10-10T02:00:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      { id: "d1", name: "Competitor Locations" },
      { id: "d2", name: "Store Sales Density" }
    ]
  },
  {
    id: "c8aa112d-3de8-45ab-92af-9ebba1e9c557",
    name: "Traffic Intensity Forecast",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_by: "Budi",
    created_at: "2025-11-05T11:15:00.000Z",
    expired_at: "2030-11-05T11:15:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      { id: "e1", name: "Traffic Flow 2025" },
      { id: "e2", name: "Main Road Network" }
    ]
  },
  {
    id: "c9aa112d-3de8-45ab-92af-9ebba1e9c558",
    name: "Store Location Feasibility",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Siti",
    created_at: "2025-09-22T08:20:00.000Z",
    expired_at: "2030-09-22T08:20:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      { id: "f1", name: "Store Sales 2024" },
      { id: "f2", name: "Competitor Catchment Area" }
    ]
  },
  {
    id: "d1aa112d-3de8-45ab-92af-9ebba1e9c559",
    name: "Consumer Behavior Trends",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Rina",
    created_at: "2025-08-01T06:15:00.000Z",
    expired_at: "2030-08-01T06:15:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [{ id: "g1", name: "Consumer Survey 2025" }]
  },
  {
    id: "d2aa112d-3de8-45ab-92af-9ebba1e9c560",
    name: "Catchment Validation Run",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_by: "Budi",
    created_at: "2025-11-11T03:30:00.000Z",
    expired_at: "2030-11-11T03:30:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [{ id: "h1", name: "Catchment Boundary Set" }]
  },
  {
    id: "e1aa112d-3de8-45ab-92af-9ebba1e9c561",
    name: "Old Customer Distribution",
    output_type: AnalysisResultType.TYPE_SITE_PROFILING,
    input_type: AnalysisJobAreaInputType.CATCHMENT,
    created_by: "Rina",
    created_at: "2024-12-01T05:00:00.000Z",
    expired_at: "2025-01-01T05:00:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [{ id: "i1", name: "Customer Location 2024" }]
  },
  {
    id: "e2aa112d-3de8-45ab-92af-9ebba1e9c562",
    name: "Legacy Market Share Study",
    output_type: AnalysisResultType.TYPE_GRID,
    input_type: AnalysisJobAreaInputType.ADMIN,
    created_by: "Andi",
    created_at: "2024-10-15T07:30:00.000Z",
    expired_at: "2024-11-15T07:30:00.000Z",
    status: AnalysisJobStatus.SUCCESS,
    datasets: [
      { id: "j1", name: "Market Share 2024" },
      { id: "j2", name: "Retail Competitor Grid" }
    ]
  }
];
